import { ImportElementRowDto, ImportElementsMappedRequest, ImportPricePreviewRequest, ImportPricePreviewRowDto } from 'api/elements/ElementsService';
import { ElementColumnMappingEntry, ElementImportRow, FIXED_IMPORT_FIELDS, PriceClassification, PriceFormulaPreview } from './types';
import { cleanElementName } from './familyMatcher';

const FIXED_FIELD_VALUES = new Set(FIXED_IMPORT_FIELDS.map(f => f.value));

/**
 * Removes IEEE-754 floating-point noise from a numeric string read at full precision from Excel
 * (e.g. "17.374500000000005" -> "17.3745"). Values that are not plain numbers (e.g. with a "€/Kg"
 * unit suffix) are left untouched.
 */
export function cleanExcelNumber (value: string | null | undefined): string {
    if (value == null) return '';
    const s = String(value).trim();
    if (s === '') return '';
    const n = Number(s);
    if (!isFinite(n)) return s;
    return String(Number(n.toPrecision(15)));
}

function getCellValue (row: ElementImportRow, colIndex: number): string {
    if (row.editedCells && row.editedCells[colIndex] !== undefined) {
        return row.editedCells[colIndex];
    }
    return row.rawCells[colIndex] ?? '';
}

/**
 * Returns the raw value (full precision) with the unit suffix from the formatted value appended.
 * e.g. raw="11.203456", formatted="11,20 €/Kg" → "11.203456 €/Kg"
 */
function getParameterCellValue (row: ElementImportRow, colIndex: number): string {
    if (row.editedCells && row.editedCells[colIndex] !== undefined) {
        return row.editedCells[colIndex];
    }
    const raw = row.rawCells[colIndex] ?? '';
    const formatted = row.formattedCells[colIndex] ?? '';

    // Extract unit suffix from formatted value (e.g. "€/Kg" from "11,20 €/Kg")
    const unitMatch = formatted.match(/€\/\w+/);
    if (unitMatch) {
        return `${raw} ${unitMatch[0]}`;
    }
    return raw;
}

function getFieldValue (row: ElementImportRow, field: string, mappings: ElementColumnMappingEntry[]): string | null {
    const mapping = mappings.find(m => m.elementField === field);
    if (!mapping) return null;

    const value = getCellValue(row, mapping.excelColumnIndex).trim();
    return value || null;
}

function buildParameterValues (row: ElementImportRow, columnMappings: ElementColumnMappingEntry[]): Record<string, string> {
    const parameterValues: Record<string, string> = {};
    for (const mapping of columnMappings) {
        if (mapping.elementField && !FIXED_FIELD_VALUES.has(mapping.elementField)) {
            const value = getParameterCellValue(row, mapping.excelColumnIndex).trim();
            if (value) {
                parameterValues[mapping.elementField] = value;
            }
        }
    }
    return parameterValues;
}

/** Index of the column mapped to the sale price, or -1 when unmapped. */
function priceColumnIndex (columnMappings: ElementColumnMappingEntry[]): number {
    return columnMappings.find(m => m.elementField === 'price')?.excelColumnIndex ?? -1;
}

/**
 * Builds the request for the price-formula preview endpoint. Includes every importable row
 * (with a family and a name) so cross-row references resolve, carrying the raw price formula
 * and computed value of each price cell.
 */
export function buildPricePreviewRequest (importRows: ElementImportRow[], columnMappings: ElementColumnMappingEntry[]): ImportPricePreviewRequest {
    const priceColIdx = priceColumnIndex(columnMappings);
    const rows: ImportPricePreviewRowDto[] = [];

    for (const row of importRows) {
        if (!row.familyId) continue;
        const rawName = getFieldValue(row, 'name', columnMappings);
        if (!rawName) continue;

        const priceFormula = priceColIdx >= 0 ? (row.formulaCells[priceColIdx] ?? '') : '';
        const priceRaw = priceColIdx >= 0 ? cleanExcelNumber(getCellValue(row, priceColIdx)) : '';
        const priceValue = priceRaw !== '' && !isNaN(Number(priceRaw)) ? Number(priceRaw) : null;

        rows.push({
            importRowIndex: row.rowIndex,
            sheetRow: row.sheetRow,
            familyId: row.familyId,
            subFamilyId: row.subFamilyId,
            elementTypeId: row.elementTypeId,
            reference: getFieldValue(row, 'reference', columnMappings),
            name: cleanElementName(rawName),
            priceFormula,
            priceValue,
            parameterValues: buildParameterValues(row, columnMappings),
        });
    }

    return {
        columnMappings: columnMappings.map(m => ({ excelColumnIndex: m.excelColumnIndex, elementField: m.elementField })),
        rows,
    };
}

export function buildImportPayload (
    importRows: ElementImportRow[],
    columnMappings: ElementColumnMappingEntry[],
    pricePreviews?: Map<number, PriceFormulaPreview>,
    priceOverrides?: Map<number, PriceClassification>
): ImportElementsMappedRequest {
    const rows: ImportElementRowDto[] = [];

    for (const row of importRows) {
        // Skip rows without family
        if (!row.familyId) continue;

        const rawName = getFieldValue(row, 'name', columnMappings);
        const name = rawName ? cleanElementName(rawName) : null;

        // Skip rows without name
        if (!name) continue;

        const parameterValues = buildParameterValues(row, columnMappings);

        // Resolve price-formula handling for this row (preview result, possibly overridden).
        const preview = pricePreviews?.get(row.rowIndex);
        const override = priceOverrides?.get(row.rowIndex);
        let priceClassification: string | null = override ?? preview?.classification ?? null;
        let priceExpressionTemplate: string | null = preview?.priceExpressionTemplate ?? null;

        // "OWN" only makes sense with a template; otherwise fall back to a fixed price.
        if (priceClassification === 'OWN' && !priceExpressionTemplate) {
            priceClassification = 'FIXED';
        }
        if (priceClassification === 'INHERIT') {
            priceExpressionTemplate = null;
        }

        rows.push({
            familyId: row.familyId,
            subFamilyId: row.subFamilyId,
            elementTypeId: row.elementTypeId,
            workTypeId: row.workTypeId,
            unitId: row.unitId,
            reference: getFieldValue(row, 'reference', columnMappings),
            name,
            fornecedor: getFieldValue(row, 'fornecedor', columnMappings),
            price: cleanExcelNumber(getFieldValue(row, 'price', columnMappings)) || null,
            additionalCoefficient: getFieldValue(row, 'additionalCoefficient', columnMappings),
            parameterValues: Object.keys(parameterValues).length > 0 ? parameterValues : undefined,
            importRowIndex: row.rowIndex,
            priceClassification,
            priceExpressionTemplate,
        });
    }

    return { rows };
}
